// REMOVE once https://haivision.jira.com/browse/HAIUI-2297 is properly resolved
import * as React from 'react';
import { Modal as RBModal } from 'react-bootstrap';
import { IHaiModalProps } from './IModal';
import styled from 'styled-components';
import classNames from 'classnames';
import { getDefaultDimensions, getMobileSize, getModalSize } from './ModalHelpers';
import { ModalContext } from './ModalContext';
import { ScreenInfoContext } from '@hai/ui-react';
import { getDimensionValue } from '../Dialog/Dialog';
import { ThemedComponent } from '@hai/ui-react/dist/ThemeContext';

const componentPath = 'components/Modal/Modal';
import './Modal-argon.less';

const BaseModal = (props: IHaiModalProps) => {
  const {
    dialogClassName = '',
    backdropClassName = '',
    className = '',
    centered = true,
    show = true,
    zindex,
    style,
    children,
    onHide = () => null,
    type,
    dynamicResizing = false,
    size = 'md',
    'data-auto': dataAuto = 'modal-dialog',
    ...rest
  } = props;
  const modalRef = React.useRef(null);
  const [modalIsOverflowing, setModalIsOverflowing] = React.useState(true);
  const screenInfoContext = React.useContext(ScreenInfoContext);
  const [modalStyle, setModalStyle] = React.useState(style);
  const defaultZIndex = 1050;
  const typeClassName = type === 'fit' ? ' modal-fit' : '';
  const paddingClassName = type === 'wide-padding' ? 'wide-padding' : '';

  const dynamicResizingValues =
    typeof size === 'string' || (size?.default && size?.mobileMode)
      ? getDefaultDimensions(dynamicResizing, size)
      : null;

  React.useEffect(() => {
    if (props.show && (!zindex || zindex !== defaultZIndex)) {
      const newStyle = {
        zIndex: zindex || defaultZIndex,
        ...style,
      };

      setModalStyle(newStyle);
    }
  }, [props.show]);

  const modalDialogClassName = `HaiModalDialog Makito ${dialogClassName} ${typeClassName} ${
    !(size === 'sm' || size === 'lg' || size === 'xl') ? (type === 'fit' ? '' : 'modal-md') : ''
  } ${!dynamicResizing ? 'static-size' : ''}`;

  const modalProps = {
    ...rest,
    backdropClassName,
    centered,
    className: `bootstrap4-iso ${className}`,
    dialogClassName: modalDialogClassName,
    show,
    size: getModalSize(props.size),
    style: modalStyle,
    'data-auto': dataAuto,
    onHide,
  };

  const modalContext = {
    onHide,
  };

  const mobileSize = `mobile-size-${getMobileSize(size)}`;
  const dialogClass = `${modalDialogClassName} ${
    screenInfoContext && `screen-${screenInfoContext.screenType}`
  } ${mobileSize}`;

  React.useEffect(() => {
    if (screenInfoContext?.mobileMode && !!modalRef?.current && show) {
      const PADDINGS = 64; // 2rem X 2
      const modalEle = modalRef.current;
      const modalHeight = modalEle.offsetHeight;
      const header = modalEle.querySelector('.HaiModalHeader');
      const bodyContent = modalEle.querySelector('.HaiModalBody');
      const footer = modalEle.querySelector('.HaiModalFooter');

      const headerHeight = header ? header.clientHeight : 0;
      const bodyHeight = bodyContent ? bodyContent.clientHeight : 0;
      const footerHeight = footer ? footer.clientHeight : 0;

      const isOverflowing = headerHeight + bodyHeight + footerHeight > modalHeight - PADDINGS;

      setModalIsOverflowing(isOverflowing);
    }
  }, [modalRef, screenInfoContext, show, props.children]);

  return (
    <ModalContext.Provider value={modalContext}>
      {dynamicResizing ? (
        <ResponsiveRBModalContainer $dynamicResizingValues={dynamicResizingValues} {...{ theme: 'argon' }}>
          <RBModal {...modalProps} dialogClassName={`${dialogClass} dynamic-sizing`}>
            <div
              ref={modalRef}
              className={classNames(
                'HaiModal',
                paddingClassName,
                screenInfoContext?.mobileMode && modalIsOverflowing && 'overflowing',
              )}
              data-auto="modal-dialog"
            >
              {props.children}
            </div>
          </RBModal>
        </ResponsiveRBModalContainer>
      ) : (
        <RBModal {...modalProps} dialogClassName={dialogClass} {...{ theme: 'argon' }}>
          <div
            ref={modalRef}
            className={classNames(
              'HaiModal',
              'static-size',
              paddingClassName,
              screenInfoContext?.mobileMode && modalIsOverflowing && 'overflowing',
            )}
            data-auto="modal-dialog"
          >
            {props.children}
          </div>
        </RBModal>
      )}
    </ModalContext.Provider>
  );
};

const ModalTitle = (props: any) => {
  const { className = '', 'data-auto': dataAuto = 'modal-title', ...rest } = props;
  return (
    <RBModal.Title {...rest} className={`HaiModalTitle ${className}`} data-auto={dataAuto}>
      {props.children}
    </RBModal.Title>
  );
};

interface IResponsiveRBModalContainerProps {
  $dynamicResizingValues: {
    minWidth: string | number;
    maxWidth: string | number;
    margin: string | number;
  };
}

const ResponsiveRBModalContainer = styled.div<IResponsiveRBModalContainerProps>`
  .HaiModalDialog::not('.screen-small') {
    @media (min-width: 0px) {
      min-width: calc(
        ${(props: any) => getDimensionValue(props.$dynamicResizingValues.minWidth)} +
          ${(props: any) => getDimensionValue(props.$dynamicResizingValues.margin)} +
          ${(props: any) => getDimensionValue(props.$dynamicResizingValues.margin)}
      );
      max-width: calc(
        ${(props: any) => getDimensionValue(props.$dynamicResizingValues.maxWidth)} +
          ${(props: any) => getDimensionValue(props.$dynamicResizingValues.margin)} +
          ${(props: any) => getDimensionValue(props.$dynamicResizingValues.margin)}
      );
    }
    .modal-content {
      margin: 0 ${(props: any) => getDimensionValue(props.$dynamicResizingValues.margin)};
      .HaiModal {
        min-width: ${(props: any) => getDimensionValue(props.$dynamicResizingValues.minWidth)};
        max-width: ${(props: any) => getDimensionValue(props.$dynamicResizingValues.maxWidth)};
      }
    }
  }
`;

interface IResponsiveRBModalContainerProps {
  $dynamicResizingValues: {
    minWidth: string | number;
    maxWidth: string | number;
    margin: string | number;
  };
}

const Modal = (props: IHaiModalProps) => ThemedComponent(BaseModal, props, componentPath);
export { Modal, ModalTitle };
